import type { TransferCertificate } from "../../../../service/transfer-certificate"

// One sheet, two halves: the school keeps the left, the family takes the right.
// They carry the same serial and the same facts — that pairing is the whole
// point, so the school's own copy can be matched against whatever a parent
// brings back years later.
//
// Laid out to match the certificate the school already issues by hand: the
// colour crest top-left of the masthead, the campus and its address beside
// it, a ruled line for every field, and both halves filling the page rather
// than sitting small in one corner.

export type PrintBranding = {
  schoolName: string
  campusName: string
  address?: string
  phone?: string
  logoUrl?: string
}

const esc = (v: unknown) =>
  String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB").replace(/\//g, "-") : ""

const ONES = ["Zero","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten",
  "Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"]
const TENS = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"]
const MONTHS = ["January","February","March","April","May","June","July","August",
  "September","October","November","December"]

const numberInWords = (n: number): string => {
  if (n < 20) return ONES[n]
  if (n < 100) return `${TENS[Math.floor(n / 10)]}${n % 10 ? " " + ONES[n % 10] : ""}`
  if (n < 1000) return `${ONES[Math.floor(n / 100)]} Hundred${n % 100 ? " " + numberInWords(n % 100) : ""}`
  return `${numberInWords(Math.floor(n / 1000))} Thousand${n % 1000 ? " " + numberInWords(n % 1000) : ""}`
}

// The certificate spells the date of birth out in words as well as figures —
// it is the field most often disputed later, and words cannot be altered with
// a pen the way digits can.
const dobInWords = (d?: string | null) => {
  if (!d) return ""
  const date = new Date(d)
  return `${numberInWords(date.getDate())} , ${MONTHS[date.getMonth()]} , ${numberInWords(date.getFullYear())}`
}

const CSS = `
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; margin: 0; color: #000; font-weight: bold; }
  @page { size: A4 landscape; margin: 6mm; }

  .sheet { display: flex; gap: 14px; align-items: stretch; height: 196mm; }

  /* The stationery has a heavy rule with a second line just inside it. Done
     with border + outline so the gap between them stays even. */
  .copy {
    border: 3px solid #000;
    outline: 1.5px solid #000;
    outline-offset: 4px;
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
  }
  .school  { width: 36%; font-size: 12px; }
  .student { width: 64%; font-size: 13.5px; }

  /* Logo top-left, name/campus/address centered in the space that's left —
     a mirror-width blank on the right keeps the text truly centered rather
     than pulled off-centre by the logo's own width. Putting the crest here
     instead of as its own centered row (the old layout) is what buys back
     the room the signature block was missing. */
  .hdr-inner { display: flex; align-items: center; gap: 8px; }
  .hdr-text { flex: 1; text-align: center; }
  .crest-img { object-fit: contain; flex: 0 0 auto; }
  /* Caps throughout the masthead, as printed. */
  .name { font-weight: bold; letter-spacing: .5px; line-height: 1.15; text-transform: uppercase; }
  .school  .name { font-size: 17px; }
  .student .name { font-size: 26px; }
  .campus { font-weight: bold; letter-spacing: .6px; text-transform: uppercase; margin-top: 2px; }
  .school  .campus { font-size: 12px; }
  .student .campus { font-size: 15px; }
  .addr { font-weight: bold; font-size: 8px; margin-top: 4px; letter-spacing: .2px; text-transform: uppercase; }
  .student .addr { font-size: 9px; }

  /* Label and value share one bordered box, as on the printed form. */
  .meta { display: flex; justify-content: space-between; align-items: center; margin: 10px 0 0; }
  .meta .box {
    border: 1.5px solid #000; padding: 3px 10px; display: flex; gap: 14px;
    align-items: baseline; font-weight: bold;
  }
  .meta .box b { font-weight: bold; }
  .school .meta .box { padding: 2px 8px; gap: 10px; font-size: 11px; }
  /* The school copy has no banner, so it still wants a little more room
     before the fields start than the student copy needs after its title box. */
  .school .meta { margin-bottom: 10px; }

  .title {
    border: 1.5px solid #000; background: #efefef; text-align: center;
    font-weight: bold; letter-spacing: 1.2px; padding: 6px 0;
    width: 58%; margin: 8px auto 14px; font-size: 12px; color: #444;
  }

  .fld { display: flex; align-items: flex-end; gap: 7px; margin: 0 0 13px; }
  .school .fld { margin-bottom: 11px; }
  .lbl { white-space: nowrap; font-weight: bold; }
  .sub { font-size: .78em; font-weight: bold; }
  .val { flex: 1; border-bottom: 1px solid #000; padding: 0 6px 2px; font-weight: bold; min-height: 1.3em; }
  .two { display: flex; gap: 22px; }
  .two .fld { flex: 1; margin-bottom: 13px; }

  .certify { font-size: .84em; margin-top: 4px; }
  /* Blank room to actually sign, directly above the line — not a stretch
     sitting earlier in the column where it does nothing for the signature. */
  .spacer { flex: 1; min-height: 34px; }
  .sign { display: flex; justify-content: space-between; gap: 26px; }
  .sign div { flex: 1; border-top: 1px solid #000; padding-top: 4px; text-align: center; font-size: .88em; }
  .school .sign { justify-content: flex-end; }
  .school .sign div { flex: 0 0 62%; }
  .copylabel { text-align: center; font-weight: normal; font-size: 8px; letter-spacing: 1.4px; margin-top: 8px; color: #777; }
`

const line = (label: string, value: string, sub?: string) =>
  `<div class="fld"><span class="lbl">${label}${
    sub ? ` <span class="sub">${sub}</span>` : ""
  }</span><span class="val">${esc(value)}</span></div>`

export const buildTCPrintHtml = (tc: TransferCertificate, branding: PrintBranding) => {
  const name = `${tc.firstName || ""} ${tc.lastName || ""}`.trim()
  const reason = tc.reasonForLeaving === "Other" ? tc.reasonForLeavingOther : tc.reasonForLeaving
  const progress = tc.progress === "Other" ? tc.progressOther : tc.progress
  const conduct = tc.conduct === "Other" ? tc.conductOther : tc.conduct
  const classNow = `${tc.classAttendingNow || tc.className}${
    tc.classProgress ? ` ( ${tc.classProgress} )` : ""
  }`

  const addressLine = [branding.address, branding.phone ? `PH: ${branding.phone}` : ""]
    .filter(Boolean)
    .join("  ")

  // The crest sits top-left of the masthead on both copies — the school copy
  // had none before, leaving that corner of the narrower half blank while the
  // student copy's crest sat centered as its own row, costing height the
  // signature block below needed.
  const head = (big: boolean) => {
    const size = big ? 60 : 42
    const logo = branding.logoUrl
      ? `<img class="crest-img" style="width:${size}px;height:${size}px" src="${esc(branding.logoUrl)}" onerror="this.style.display='none'" />`
      : ""
    // A same-sized blank on the right balances the logo's width so the name
    // still centres on the whole masthead, not just the space left of it.
    const mirror = branding.logoUrl
      ? `<div class="crest-img" style="width:${size}px;visibility:hidden"></div>`
      : ""
    return `
    <div class="hdr-inner">
      ${logo}
      <div class="hdr-text">
        <div class="name">${esc(branding.schoolName)}</div>
        <div class="campus">${esc(branding.campusName)}</div>
        ${addressLine ? `<div class="addr">${esc(addressLine)}</div>` : ""}
      </div>
      ${mirror}
    </div>
    <div class="meta">
      <span class="box">Serial No. <b>${esc(tc.serialNo)}</b></span>
      <span class="box">G.R. No. <b>${esc(tc.grNumber)}</b></span>
    </div>`
  }

  const fields = (big: boolean) => `
    ${line("Name of the Student", name)}
    ${line("Father's Name", tc.fatherName)}
    ${line("Place of Birth", tc.placeOfBirth)}
    ${line("Date of Birth", fmt(tc.dateOfBirth), "(in figures)")}
    ${line("", dobInWords(tc.dateOfBirth), "(in words)")}
    ${line("Date of Admission", fmt(tc.dateOfAdmission))}
    ${line("Previous School attended", tc.previousSchool)}
    ${line("Class attending now", classNow)}
    ${big
      ? `<div class="two">${line("Progress:", progress)}${line("Conduct:", conduct)}</div>`
      : `${line("Progress:", progress)}${line("Conduct:", conduct)}`}
    ${line("Fee Paid up To", tc.feePaidUpTo)}
    ${line("Date of leaving the school", fmt(tc.dateOfLeaving))}
    ${line("Reason for leaving the school", reason)}`

  const studentTail = `
    <div class="certify">Certified that the above informations are in accordance with the School General Register ( G. R. ).</div>
    <div class="fld" style="max-width:52%"><span class="lbl">Date</span><span class="val">${fmt(tc.issueDate)}</span></div>
    <div class="spacer"></div>
    <div class="sign"><div>Class Teacher</div><div>Principal</div></div>`

  const schoolTail = `
    ${line("Issued By", tc.issuedByName)}
    ${line("Date", fmt(tc.issueDate))}
    ${line("Received By", "")}
    <div class="spacer"></div>
    <div class="sign"><div>Signature</div></div>`

  return `<html><head><title>TC ${esc(tc.serialNo)}</title><style>${CSS}</style></head>
    <body>
      <div class="sheet">
        <div class="copy school">${head(false)}${fields(false)}${schoolTail}<div class="copylabel">SCHOOL COPY</div></div>
        <div class="copy student">${head(true)}<div class="title">SCHOOL LEAVING CERTIFICATE</div>${fields(true)}${studentTail}<div class="copylabel">STUDENT COPY</div></div>
      </div>
    </body></html>`
}

export const printTransferCertificate = (
  tc: TransferCertificate,
  branding: PrintBranding
) => {
  const win = window.open("", "_blank")
  if (!win) return false
  win.document.write(buildTCPrintHtml(tc, branding))
  win.document.close()
  let started = false
  const start = () => {
    if (started) return
    started = true
    win.focus()
    win.print()
  }
  win.onafterprint = () => win.close()
  win.onload = start
  // The crest has to load before the preview renders, or it prints missing.
  setTimeout(start, 900)
  return true
}
